import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { useLang } from "@/lib/LangContext";

export const Hero = () => {
  const { t } = useLang();

  const stats = [
    { value: "120+", label: t.hero.stat_1 },
    { value: "99.9%", label: t.hero.stat_2 },
    { value: "14", label: t.hero.stat_3 },
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-16 flex flex-col justify-center overflow-hidden border-b border-white/10"
    >
      {/* Dot grid + mesh background */}
      <div
        className="absolute inset-0 -z-0 opacity-[0.15]"
        style={{
          backgroundImage:
            "radial-gradient(circle at center, rgba(0,240,255,0.5) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />
      <div
        className="absolute inset-0 -z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 30%, rgba(0,240,255,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(0,240,255,0.06) 0%, transparent 55%)",
        }}
      />

      {/* Animated slow-rotating ring */}
      <div className="pointer-events-none absolute top-1/2 right-[-200px] -translate-y-1/2 -z-0 slow-rotate">
        <div className="w-[600px] h-[600px] rounded-full border border-brand-600/10 relative">
          {/* Anillo medio */}
          <div className="absolute inset-8 rounded-full border border-brand-600/10" />
          {/* Anillo interior */}
          <div className="absolute inset-20 rounded-full border border-brand-600/10" />

          {/* Cuadritos del anillo exterior (600px) */}
          <div className="absolute top-0 left-1/2 w-2 h-2 bg-brand-500 -translate-x-1/2 -translate-y-1" />
          <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-brand-500 -translate-x-1/2 translate-y-1" />
          <div className="absolute top-1/2 right-0 w-2 h-2 bg-brand-500 translate-x-1 -translate-y-1/2" />
          <div className="absolute top-1/2 left-0 w-1.5 h-1.5 bg-brand-400/60 -translate-x-1 -translate-y-1/2" />

          {/* Cuadritos del anillo medio (inset-8 ≈ 568px) */}
          <div className="absolute top-8 left-1/2 w-2 h-2 bg-brand-500/80 -translate-x-1/2 -translate-y-1" />
          <div className="absolute bottom-8 right-1/4 w-1.5 h-1.5 bg-brand-500/70 translate-y-1" />
          <div className="absolute top-1/4 right-8 w-2 h-2 bg-brand-400/80 translate-x-0.5" />
          <div className="absolute bottom-1/4 left-8 w-1.5 h-1.5 bg-brand-500/60 -translate-x-0.5" />

          {/* Cuadritos del anillo interior (inset-20 ≈ 520px) */}
          <div className="absolute top-20 left-1/2 w-2 h-2 bg-brand-500 -translate-x-1/2" />
          <div className="absolute bottom-20 left-1/2 w-1.5 h-1.5 bg-brand-400/70 -translate-x-1/2" />
          <div className="absolute top-1/2 right-20 w-2 h-2 bg-brand-500/80 -translate-y-1/2" />
          <div className="absolute top-1/3 left-20 w-1.5 h-1.5 bg-brand-400/60 -translate-y-1/2" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl"
        >
          <p
            data-testid="hero-overline"
            className="font-mono-x text-xs tracking-[0.3em] text-brand-500 mb-6"
          >
            {t.hero.overline}
          </p>
          <h1
            data-testid="hero-title"
            className="font-display font-medium tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
          >
            {t.hero.title_1}
            <br />
            <span className="text-brand-500">{t.hero.title_2}</span>
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mt-8 font-mono-x text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              data-testid="hero-cta-primary"
              href="#contact"
              className="group inline-flex items-center gap-2 bg-white text-black px-6 py-3 font-mono-x text-xs uppercase tracking-widest font-bold hover:bg-brand-500 transition-colors duration-200"
            >
              {t.hero.cta_primary}
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform duration-200"
              />
            </a>
            <a
              data-testid="hero-cta-secondary"
              href="#products"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 font-mono-x text-xs uppercase tracking-widest hover:border-brand-500 hover:text-brand-500 transition-colors duration-200"
            >
              {t.hero.cta_secondary}
              <ArrowDown size={16} />
            </a>
          </div>
        </motion.div>

        <div className="mt-24 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * i, duration: 0.5 }}
              className="relative bg-black/90 backdrop-blur-sm px-6 py-6 overflow-hidden"
              data-testid={`hero-stat-${i}`}
            >
              {/* Rotating squares decoration */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8 + i * 2, repeat: Infinity, ease: "linear" }}
                className="absolute top-3 right-3 w-8 h-8 border border-brand-500/40"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 12 + i * 2, repeat: Infinity, ease: "linear" }}
                className="absolute top-5 right-5 w-4 h-4 border border-brand-500/60"
              />
              <div className="font-display text-3xl sm:text-4xl text-white tracking-tight">
                {s.value}
              </div>
              <div className="mt-2 font-mono-x text-[10px] tracking-[0.2em] uppercase text-slate-400">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
